import {FileSystemApi} from '@/common/api/FileSystemApi';
import {useApiClient} from '@/common/api/useApiClient';
import {useStore} from '@/common/store/store';
import {useAuth} from '@/composables/useAuth';
import {computed, markRaw, ref} from 'vue';
import * as tus from 'tus-js-client';

// T00V7O2QY41Z — global upload queue. It lives outside any page, so a running
// upload survives every navigation inside the app; the upload bar in the main
// layout shows it. Uploads run strictly one after another (Epic E00V4AMVBXF5).

export type UploadStatus = 'waiting' | 'uploading' | 'processing' | 'done' | 'failed' | 'noSpace' | 'cancelled';

export type UploadEntry = {
  id: number;
  file: File;
  // Target folder (uuid); root when empty.
  folder?: string;
  status: UploadStatus;
  progress: number;
  // Estimated seconds left for the running upload; null until there is a rate.
  remainingSeconds: number | null;
};

// Error codes of the API meaning "the file does not fit" (T00OMH7Z8ECO).
const STORAGE_ERROR_CODES = ['FILE_SYSTEM_STORAGE_LIMIT', 'TRIAL_LIMIT_STORAGE'];
const ACTIVE: UploadStatus[] = ['waiting', 'uploading', 'processing'];

export const uploadEntries = ref<UploadEntry[]>([]);
// Increases with every finished upload; the file list watches it to reload.
// (The event bus only exists inside components, this module does not.)
export const finishedUploads = ref(0);
// Set when the storage is full: the rest of the queue waits until a retry.
export const queuePaused = ref(false);

export const hasActiveUploads = computed(() => uploadEntries.value.some(entry => ACTIVE.includes(entry.status)));

let nextId = 1;
let running: {id: number, upload: tus.Upload} | null = null;

export function enqueueUploads(files: File[], folder?: string): void {
  for (const file of files) {
    uploadEntries.value.push({
      id: nextId++,
      // A reactive proxy around the File would break tus reading its slices.
      file: markRaw(file),
      folder,
      status: 'waiting',
      progress: 0,
      remainingSeconds: null,
    });
  }
  startNext();
}

// Bytes of uploads that have not reached the server yet; the server only
// counts uploads it has already accepted as reserved.
export function queuedBytes(): number {
  return uploadEntries.value
    .filter(entry => entry.status === 'waiting')
    .reduce((sum, entry) => sum + entry.file.size, 0);
}

// Names in the queue that will exist soon, so a new file cannot take them.
export function queuedNames(): string[] {
  return uploadEntries.value
    .filter(entry => ACTIVE.includes(entry.status))
    .map(entry => entry.file.name.replace(/ /g, '_'));
}

export function cancelUpload(id: number): void {
  const entry = findEntry(id);
  if (!entry || !['waiting', 'uploading'].includes(entry.status)) {
    return;
  }
  if (running?.id === id) {
    void running.upload.abort();
    running = null;
  }
  entry.status = 'cancelled';
  entry.remainingSeconds = null;
  startNext();
}

export function retryUpload(id: number): void {
  const entry = findEntry(id);
  if (!entry || !['failed', 'noSpace', 'cancelled'].includes(entry.status)) {
    return;
  }
  entry.status = 'waiting';
  entry.progress = 0;
  queuePaused.value = false;
  startNext();
}

// Drops everything that is no longer running; used when the bar is closed.
export function clearFinishedUploads(): void {
  uploadEntries.value = uploadEntries.value.filter(entry => ACTIVE.includes(entry.status));
}

function findEntry(id: number): UploadEntry | undefined {
  return uploadEntries.value.find(entry => entry.id === id);
}

function startNext(): void {
  if (running || queuePaused.value) {
    return;
  }
  const next = uploadEntries.value.find(entry => entry.status === 'waiting');
  if (next) {
    start(next);
  }
}

function finish(entry: UploadEntry, status: UploadStatus): void {
  entry.status = status;
  entry.remainingSeconds = null;
  if (running?.id === entry.id) {
    running = null;
  }
  if (status === 'done') {
    finishedUploads.value++;
  }
  startNext();
}

function isStorageError(error: Error): boolean {
  const response = (error as tus.DetailedError).originalResponse;
  if (response?.getStatus() !== 403) {
    return false;
  }
  try {
    const body = JSON.parse(response.getBody() ?? '{}') as {errorCode?: string};
    return STORAGE_ERROR_CODES.includes(body.errorCode ?? '');
  } catch {
    return false;
  }
}

// Video metadata and previews; "done" only after this step.
async function compress(fileId: string): Promise<void> {
  // A failed preview is not a failed upload: the file is stored, and the
  // preview can be recreated from the file menu.
  await useApiClient().post(FileSystemApi.compress(), {id: fileId}).catch(() => undefined);
}

function start(entry: UploadEntry): void {
  const store = useStore();
  const {refresh} = useAuth();
  const startedAt = Date.now();
  let fileId: string | null = null;
  // tus's onShouldRetry is sync: on a 401 it only marks the refresh, the async
  // onBeforeRequest of the retry awaits it and stamps the new token. A large
  // upload easily outlives the access token.
  let needsAuthRefresh = false;

  entry.status = 'uploading';
  const upload = new tus.Upload(entry.file, {
    endpoint: FileSystemApi.handle(),
    metadata: {
      filename: entry.file.name,
      filetype: entry.file.type,
      ...(entry.folder ? {folder: entry.folder} : {}),
    },
    chunkSize: 2500000,
    retryDelays: [0, 1000, 3000, 5000, 10000],
    onBeforeRequest: async (request) => {
      if (needsAuthRefresh) {
        needsAuthRefresh = false;
        await refresh();
      }
      request.setHeader('Authorization', `Bearer ${store.accessToken}`);
    },
    onShouldRetry: (error) => {
      const status = error.originalResponse?.getStatus();
      if (status === 401) {
        needsAuthRefresh = true;
        return true;
      }
      // tus default: retry on network errors and 5xx, surface 4xx at once.
      return status == null || (status >= 500 && status < 600);
    },
    onProgress: (bytesSent, bytesTotal) => {
      entry.progress = bytesTotal > 0 ? Math.round(bytesSent / bytesTotal * 100) : 0;
      const seconds = (Date.now() - startedAt) / 1000;
      entry.remainingSeconds = bytesSent > 0 && seconds > 0 ? Math.round((bytesTotal - bytesSent) / (bytesSent / seconds)) : null;
    },
    onAfterResponse: (request, response) => {
      // The last PATCH answers with the uuid of the new file.
      if (request.getMethod() === 'PATCH' && response.getStatus() === 200) {
        fileId = response.getBody();
      }
    },
    onSuccess: async () => {
      entry.status = 'processing';
      entry.progress = 100;
      if (fileId) {
        await compress(fileId);
      }
      finish(entry, 'done');
    },
    onError: (error) => {
      if (isStorageError(error)) {
        queuePaused.value = true;
        finish(entry, 'noSpace');
        return;
      }
      finish(entry, 'failed');
    },
  });
  running = {id: entry.id, upload};
  upload.start();
}
